import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Users, CheckCircle, BookOpen, Newspaper, Award } from 'lucide-react'
import type { Activity } from '../../types/dashboard.types'

interface ActivityFeedProps {
    activities: Activity[]
    loading?: boolean
}

const ActivityFeed = ({ activities, loading }: ActivityFeedProps) => {
    const getIcon = (type: Activity['type']) => {
        switch (type) {
            case 'user':
                return <Users className="w-5 h-5" />
            case 'test':
                return <CheckCircle className="w-5 h-5" />
            case 'aula':
                return <BookOpen className="w-5 h-5" />
            case 'noticia':
                return <Newspaper className="w-5 h-5" />
            case 'resultado':
                return <Award className="w-5 h-5" />
            default:
                return <CheckCircle className="w-5 h-5" />
        }
    }

    const getColorClasses = (color: string) => {
        const colors: Record<string, string> = {
            blue: 'bg-blue-100 text-blue-600',
            green: 'bg-green-100 text-green-600',
            yellow: 'bg-yellow-100 text-yellow-600',
            red: 'bg-red-100 text-red-600',
            purple: 'bg-purple-100 text-purple-600',
            indigo: 'bg-indigo-100 text-indigo-600'
        }
        return colors[color] || colors.blue
    }

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-bizu p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Atividades Recentes</h3>
                <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-start gap-3 animate-pulse">
                            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                            <div className="flex-1">
                                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (activities.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-bizu p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Atividades Recentes</h3>
                <p className="text-gray-500 text-center py-8">Nenhuma atividade recente</p>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-lg shadow-bizu p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Atividades Recentes</h3>
            <div className="space-y-4">
                {activities.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3">
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${getColorClasses(activity.color)}`}>
                            {getIcon(activity.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                {formatDistanceToNow(new Date(activity.timestamp), {
                                    addSuffix: true,
                                    locale: ptBR
                                })}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default ActivityFeed
